// Dev-only checkout API client.
//
// HARD BOUNDARIES (enforced by code review + tests):
// - Only used when VITE_ENABLE_CHECKOUT_DEV_API === "true".
// - Only POSTs /api/checkout/quote and GETs /api/checkout/{id}/status on the
//   dev base URL (default https://paworld-content-api-dev.azurewebsites.net).
// - Does NOT call:
//     * confirm, payment, sessions, internal admin quote
//     * production api2.paworld365.com
//     * Stripe, Dataverse
// - Does NOT compute tax. Money/tax values are taken verbatim from the API
//   response. The FE only formats them for display.
// - Does NOT echo any incoming GUID-shaped string; a defensive guard scrubs
//   response strings that match a GUID pattern.

import type {
  CheckoutQuoteRequest,
  CheckoutQuoteResponse,
} from "./quoteTypes";
import { assertCheckoutMutationAllowed } from "./productionMode";

const DEFAULT_DEV_BASE = "https://paworld-content-api-dev.azurewebsites.net";

export const CHECKOUT_DEV_API_ENABLED =
  String(import.meta.env.VITE_ENABLE_CHECKOUT_DEV_API ?? "false").toLowerCase() === "true";

export const CHECKOUT_DEV_API_BASE_URL = String(
  import.meta.env.VITE_CHECKOUT_DEV_API_BASE_URL ?? DEFAULT_DEV_BASE,
).replace(/\/+$/, "");

const GUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

/** Recursively replace any GUID-shaped string with "[redacted-guid]". */
export function scrubGuids<T>(value: T): T {
  if (value == null) return value;
  if (typeof value === "string") {
    return (GUID_RE.test(value) ? "[redacted-guid]" : value) as unknown as T;
  }
  if (Array.isArray(value)) {
    return value.map(scrubGuids) as unknown as T;
  }
  if (typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = scrubGuids(v);
    }
    return out as unknown as T;
  }
  return value;
}

function assertDevBaseUrl(url: string): void {
  // Defence-in-depth: never let this client point at production.
  if (/api2\.paworld365\.com/i.test(url)) {
    throw new Error("checkout dev API client must not target production");
  }
}

export class CheckoutDevApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown, message: string) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

function buildQuoteBody(req: CheckoutQuoteRequest): Record<string, unknown> {
  const ctx = req.buyerContext;
  const buyerCountry = (ctx?.countryCode ?? req.buyer?.countryCode ?? "").toUpperCase();
  const billingCountry = (ctx?.billingCountryCode ?? buyerCountry ?? "").toUpperCase();
  // Defensive: never forward GUID-shaped IDs as offering/PBE public IDs.
  const items = (req.items ?? []).map((i) => ({
    cartLineKey: i.cartLineKey,
    offeringPublicId: i.offeringPublicId,
    priceBookEntryPublicId: i.priceBookEntryPublicId,
    quantity: i.quantity,
  }));
  for (const i of items) {
    if (GUID_RE.test(i.offeringPublicId) || GUID_RE.test(i.priceBookEntryPublicId)) {
      throw new Error("checkout dev quote: refusing to send GUID-shaped IDs");
    }
  }
  // Canonical quote wire contract — same shape used by the production
  // Stage 2 client. No countryCode/billingCountryCode/isCompany/vatValidated.
  const isCompany = Boolean(ctx?.isCompany ?? req.buyer?.isCompany ?? false);
  const vatNumber = ctx?.vatNumber ?? req.buyer?.vatNumber;
  const buyerContext: Record<string, unknown> = {
    buyerCountryIso: buyerCountry,
    billingCountryIso: billingCountry,
    customerType: ctx?.customerType ?? (isCompany ? "business" : "unknown"),
    vatValidationStatus: ctx?.vatValidationStatus ?? "not_provided",
    vatValidationSource: ctx?.vatValidationSource ?? "none",
  };
  if (vatNumber && String(vatNumber).trim()) buyerContext.vatNumber = String(vatNumber).trim();
  return { items, buyerContext };
}

export async function devQuoteCheckout(
  req: CheckoutQuoteRequest,
  init?: { fetchImpl?: typeof fetch; baseUrl?: string },
): Promise<CheckoutQuoteResponse> {
  assertCheckoutMutationAllowed("POST /api/checkout/quote (dev client)");
  const base = (init?.baseUrl ?? CHECKOUT_DEV_API_BASE_URL).replace(/\/+$/, "");
  assertDevBaseUrl(base);
  const fetchImpl = init?.fetchImpl ?? fetch;
  const body = buildQuoteBody(req);
  const res = await fetchImpl(`${base}/api/checkout/quote`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(body),
  });
  const text = await res.text();
  let parsed: unknown = null;
  try { parsed = text ? JSON.parse(text) : null; } catch { parsed = text; }
  if (!res.ok) {
    throw new CheckoutDevApiError(res.status, parsed, `dev quote failed: ${res.status}`);
  }
  return scrubGuids(parsed) as CheckoutQuoteResponse;
}

export interface CheckoutStatusResponse {
  checkoutSessionPublicId: string;
  state?: string;
  nextAction?: string | null;
  [key: string]: unknown;
}

export async function devGetCheckoutStatus(
  checkoutSessionPublicId: string,
  init?: { fetchImpl?: typeof fetch; baseUrl?: string },
): Promise<CheckoutStatusResponse> {
  if (!checkoutSessionPublicId) throw new Error("checkoutSessionPublicId required");
  if (GUID_RE.test(checkoutSessionPublicId)) {
    throw new Error("checkout dev status: GUID-shaped id rejected");
  }
  const base = (init?.baseUrl ?? CHECKOUT_DEV_API_BASE_URL).replace(/\/+$/, "");
  assertDevBaseUrl(base);
  const fetchImpl = init?.fetchImpl ?? fetch;
  const res = await fetchImpl(
    `${base}/api/checkout/${encodeURIComponent(checkoutSessionPublicId)}/status`,
    { method: "GET", headers: { Accept: "application/json" } },
  );
  const text = await res.text();
  let parsed: unknown = null;
  try { parsed = text ? JSON.parse(text) : null; } catch { parsed = text; }
  if (!res.ok) {
    throw new CheckoutDevApiError(res.status, parsed, `dev status failed: ${res.status}`);
  }
  return scrubGuids(parsed) as CheckoutStatusResponse;
}
